import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Clock, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { visaServices } from '../data/mock';

const Services = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 via-blue-700 to-teal-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">
              Our Immigration Services
            </h1>
            <p className="text-xl text-blue-100 leading-relaxed">
              Comprehensive visa solutions tailored to your goals - from tourist visits to permanent residence
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {visaServices.map((service, idx) => (
              <div 
                key={service.id} 
                id={service.slug}
                className={`scroll-mt-24 ${idx % 2 === 0 ? '' : 'bg-gradient-to-br from-gray-50 to-blue-50'} rounded-2xl p-8 lg:p-12`}
              >
                <div className="grid lg:grid-cols-2 gap-8 items-center">
                  {/* Content */}
                  <div className={idx % 2 === 0 ? '' : 'lg:order-2'}>
                    {service.specialization && (
                      <div className="mb-4">
                        <span className="bg-gradient-to-r from-blue-600 to-teal-600 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-md">
                          ⭐ Our Specialization
                        </span>
                      </div>
                    )}
                    <div className="text-4xl mb-4">{service.country.split(' ')[0]}</div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">
                      {service.title}
                    </h2>
                    <p className="text-lg text-gray-700 mb-6">
                      {service.description}
                    </p>

                    {/* Details Grid */}
                    <div className="grid sm:grid-cols-2 gap-4 mb-6">
                      <div className="flex items-start gap-3">
                        <Users className="h-5 w-5 text-blue-600 flex-shrink-0 mt-1" />
                        <div>
                          <p className="font-semibold text-gray-900 text-sm">Who It's For</p>
                          <p className="text-gray-600 text-sm">{service.whoFor}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <Clock className="h-5 w-5 text-blue-600 flex-shrink-0 mt-1" />
                        <div>
                          <p className="font-semibold text-gray-900 text-sm">Processing Time</p>
                          <p className="text-gray-600 text-sm">{service.processingTime}</p>
                        </div>
                      </div>
                    </div>

                    {/* Visa Types */}
                    <div className="mb-6">
                      <p className="font-semibold text-gray-900 mb-3">We Assist With:</p>
                      <div className="space-y-2">
                        {service.types.map((type, typeIdx) => (
                          <div key={typeIdx} className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                            <span className="text-gray-700">{type}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-4">
                      <Button asChild className="bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 text-white">
                        <Link to="/eligibility">
                          Check Eligibility
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Link>
                      </Button>
                      <Button asChild variant="outline" className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50">
                        <Link to="/contact">
                          Contact Us
                        </Link>
                      </Button>
                    </div>
                  </div>

                  {/* Image */}
                  <div className={idx % 2 === 0 ? '' : 'lg:order-1'}>
                    <Card className="overflow-hidden border-2 border-gray-200">
                      <img
                        src={
                          idx === 0 
                            ? "https://images.pexels.com/photos/8292854/pexels-photo-8292854.jpeg"
                            : "https://images.unsplash.com/photo-1764863369499-714d87f63ef4"
                        }
                        alt={service.title}
                        className="w-full h-[350px] object-cover"
                      />
                    </Card>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Our Services */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Our Services
            </h2>
            <p className="text-lg text-gray-600">
              Professional immigration assistance backed by expertise and ethics
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-2 border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-100 to-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-7 w-7 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Accurate Documentation</h3>
                <p className="text-gray-600">
                  Meticulous preparation to minimize errors and maximize approval chances
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-100 to-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="h-7 w-7 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Personalized Approach</h3>
                <p className="text-gray-600">
                  Every case is unique - we tailor our advice to your specific profile
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-gradient-to-br from-blue-100 to-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="h-7 w-7 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Timely Updates</h3>
                <p className="text-gray-600">
                  Regular communication and status updates throughout the process
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-teal-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Begin Your Visa Application?
          </h2>
          <p className="text-lg text-blue-100 mb-8">
            Let's assess your eligibility and create your personalized pathway
          </p>
          <Button asChild size="lg" className="bg-white text-blue-600 hover:bg-gray-100 text-base">
            <Link to="/eligibility">
              Start Free Eligibility Assessment
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Services;
